import { useState } from "react"

import { Button } from "@/components/ui/button"
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { formatRelativeTime } from "@/lib/utils"
import type { AgentPromptVersion } from "@/types"

interface AgentPromptVersionsProps {
  versions: AgentPromptVersion[]
  onRestore: (prompt: string) => void
}

export default function AgentPromptVersions({
  versions,
  onRestore,
}: AgentPromptVersionsProps) {
  const [viewingVersion, setViewingVersion] =
    useState<AgentPromptVersion | null>(null)

  if (versions.length === 0) return null

  return (
    <div className="space-y-2">
      <p className="text-muted-foreground text-sm font-medium">
        Version history
      </p>
      <div className="divide-border divide-y rounded-md border">
        {versions.map((version) => (
          <div
            key={version.uid}
            className="flex items-center justify-between gap-4 px-3 py-2"
          >
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2">
                <span className="text-sm font-medium">
                  v{version.version_number}
                </span>
                <span className="text-muted-foreground text-xs">
                  {formatRelativeTime(version.created_at)}
                </span>
                {version.changer_name && (
                  <span className="text-muted-foreground text-xs">
                    by {version.changer_name}
                  </span>
                )}
              </div>
              <p className="text-muted-foreground truncate text-xs">
                {version.system_prompt.slice(0, 100)}
                {version.system_prompt.length > 100 ? "..." : ""}
              </p>
            </div>
            <div className="flex shrink-0 gap-1">
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => setViewingVersion(version)}
              >
                View
              </Button>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => onRestore(version.system_prompt)}
              >
                Restore
              </Button>
            </div>
          </div>
        ))}
      </div>

      <Dialog
        open={viewingVersion !== null}
        onOpenChange={(open) => {
          if (!open) setViewingVersion(null)
        }}
      >
        <DialogContent className="max-h-[80vh] max-w-2xl overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Version {viewingVersion?.version_number}</DialogTitle>
          </DialogHeader>
          <pre className="bg-muted rounded-md p-4 text-sm whitespace-pre-wrap">
            {viewingVersion?.system_prompt}
          </pre>
          <div className="flex justify-end">
            <Button
              type="button"
              variant="outline"
              onClick={() => {
                if (viewingVersion) {
                  onRestore(viewingVersion.system_prompt)
                  setViewingVersion(null)
                }
              }}
            >
              Restore this version
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  )
}
